import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Check, ChevronDown, Eye, EyeOff, Pin } from "lucide-react";
import useMediaQuery from "@mui/material/useMediaQuery";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import { extractRowsFromResponse } from "./datagridConfigUtils";

const SORT_OPTIONS = [
  { value: "", label: "None" },
  { value: "asc", label: "Asc" },
  { value: "desc", label: "Desc" },
];

function EmsCheck({ checked, indeterminate, disabled, onChange, title, testId }) {
  const className = [
    "ems-pv-check",
    checked ? "on" : "",
    indeterminate && !checked ? "mixed" : "",
    disabled ? "is-disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={className}
      title={title}
      disabled={disabled}
      data-testid={testId}
      aria-pressed={!!checked}
      onClick={() => {
        if (disabled) return;
        onChange(!checked);
      }}
    >
      {checked ? <Check size={12} strokeWidth={3} /> : null}
      {indeterminate && !checked ? <span className="ems-pv-dash" /> : null}
    </button>
  );
}

const TableConfigForm = ({
  apiFieldsConfig,
  apiPreviewConfig,
  datagridKeyProp,
  tablename,
  tableheading,
}) => {
  const { userData } = useAuthStore();
  const org = userData?.organization;

  // STATE
  const [columns, setColumns] = useState([]);
  const [initialColumns, setInitialColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loadingFields, setLoadingFields] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [sortModel, setSortModel] = useState(null);
  const [filterModel, setFilterModel] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);
  const [existingConfigId, setExistingConfigId] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [schemaDiff] = useState({
    missingMandatory: [],
    missingVisible: [],
    missingPinned: [],
  });

  // Added: Default sorting for all columns (dropdown in header)
  const [defaultSortOrderAll, setDefaultSortOrderAll] = useState("");

  const inputRefs = useRef({});
  const checkboxRefs = useRef({});
  const focusTracker = useRef({});
  const initialDataLoaded = useRef(false);

  // COMPUTED VALUES
  const liveTableConfig = useMemo(() => {
    if (!columns || !Array.isArray(columns)) return null;
    return {
      mandatoryColumns: columns
        .filter((col) => col.required && col.field !== "id")
        .map((col) => col.field),
      defaultVisibleColumns: columns
        .filter((col) => col.visible)
        .map((col) => col.field),
      pinnedColumns: columns
        .filter((col) => col.pinned === "left")
        .map((col) => col.field),
      sort: sortModel,
      filters: filterModel,
      pagination: { rowsPerPage: 10 },
      search: { enabled: true, placeholder: "Search...", fields: [] },
      defaultSortOrderAll, // For saving
    };
  }, [columns, sortModel, filterModel, defaultSortOrderAll]);

  const {
    allVisible,
    someVisible,
    allFilterable,
    someFilterable,
    allSortable,
    someSortable,
    visibleColumns,
  } = useMemo(() => {
    const visibleCols = columns.filter((c) => c.visible);
    const visibleCount = visibleCols.length;

    return {
      allVisible: columns.length > 0 && columns.every((c) => c.visible),
      someVisible: columns.some((c) => c.visible),
      visibleColumns: visibleCols,
      allFilterable: visibleCount > 0 && visibleCols.every((c) => c.filterable),
      someFilterable: visibleCols.some((c) => c.filterable),
      allSortable: visibleCount > 0 && visibleCols.every((c) => c.sortable),
      someSortable: visibleCols.some((c) => c.sortable),
    };
  }, [columns]);

  const pinnedSummary = useMemo(
    () => ({ left: columns.filter((c) => c.pinned === "left").length }),
    [columns]
  );

  const tableKey = useMemo(
    () =>
      `table-${columns.length}-${JSON.stringify(
        columns.map((c) => ({
          f: c.field,
          v: c.visible,
          w: c.width,
          p: c.pinned,
          l: c.label,
        }))
      )}`,
    [columns]
  );

  // FOCUS PRESERVATION (unchanged)
  const preserveFocusAndPosition = useCallback(
    (field, elementType, callback) => {
      const elementKey = `${field}-${elementType}`;
      const inputRef = inputRefs.current[elementKey];
      const checkboxRef = checkboxRefs.current[elementKey];

      let cursorPosition = null;
      let wasFocused = false;
      let wasActiveElement = false;
      let selectionStart = null;
      let selectionEnd = null;

      if (inputRef && document.activeElement === inputRef) {
        cursorPosition = inputRef.selectionStart ?? null;
        selectionStart = inputRef.selectionStart ?? null;
        selectionEnd = inputRef.selectionEnd ?? null;
        wasFocused = true;
        wasActiveElement = true;
        focusTracker.current[elementKey] = {
          cursorPosition,
          selectionStart,
          selectionEnd,
          wasFocused,
          type: "input",
        };
      }

      if (checkboxRef && document.activeElement === checkboxRef) {
        wasFocused = true;
        wasActiveElement = true;
        focusTracker.current[elementKey] = { wasFocused, type: "checkbox" };
      }

      callback();

      if (wasActiveElement) {
        setTimeout(() => {
          const trackedData = focusTracker.current[elementKey];
          if (
            trackedData?.type === "input" &&
            inputRef &&
            trackedData.wasFocused
          ) {
            inputRef.focus();
            if (
              trackedData.cursorPosition !== null &&
              trackedData.cursorPosition !== undefined
            ) {
              inputRef.setSelectionRange(
                trackedData.selectionStart ?? trackedData.cursorPosition,
                trackedData.selectionEnd ?? trackedData.cursorPosition
              );
            }
          }
          if (
            trackedData?.type === "checkbox" &&
            checkboxRef &&
            trackedData.wasFocused
          ) {
            checkboxRef.focus();
          }
          delete focusTracker.current[elementKey];
        }, 0);
      }
    },
    []
  );

  // COLUMN UPDATE HANDLERS (unchanged)
  const updateByField = useCallback(
    (field, property, value, elementType = "checkbox") => {
      preserveFocusAndPosition(field, elementType, () => {
        setColumns((prev) => {
          const idx = prev.findIndex((c) => c.field === field);
          if (idx === -1) return prev;
          const col = prev[idx];

          if (property === "visible" && value === false) {
            const updated = [...prev];
            updated[idx] = {
              ...col,
              visible: false,
              filterable: false,
              sortable: false,
              pinned: "none",
              required: false,
            };
            return updated;
          }

          const updated = [...prev];
          updated[idx] = { ...col, [property]: value };
          return updated;
        });
      });
    },
    [preserveFocusAndPosition]
  );

  const handleWidthChange = useCallback(
    (field, value) => {
      const numericValue = Number.parseInt(value, 10) || 40;
      preserveFocusAndPosition(field, "width", () => {
        updateByField(field, "width", numericValue, "width");
      });
    },
    [updateByField, preserveFocusAndPosition]
  );

  const handleLabelChange = useCallback(
    (field, value) => {
      preserveFocusAndPosition(field, "label", () => {
        updateByField(field, "label", value, "label");
      });
    },
    [updateByField, preserveFocusAndPosition]
  );

  const handleVisibilityChange = useCallback(
    (field, checked) => {
      updateByField(field, "visible", checked, "visible");
    },
    [updateByField]
  );

  const handleFilterableChange = useCallback(
    (field, checked) => {
      updateByField(field, "filterable", checked, "filterable");
    },
    [updateByField]
  );

  const handleSortableChange = useCallback(
    (field, checked) => {
      updateByField(field, "sortable", checked, "sortable");
    },
    [updateByField]
  );

  const handlePinnedChange = useCallback(
    (field, checked) => {
      updateByField(field, "pinned", checked ? "left" : "none", "pinned");
    },
    [updateByField]
  );

  const toggleRequired = useCallback(
    (field) => {
      preserveFocusAndPosition(field, "required-chip", () => {
        setColumns((prev) => {
          const idx = prev.findIndex((c) => c.field === field);
          if (idx === -1) return prev;
          const updated = [...prev];
          updated[idx] = { ...updated[idx], required: !updated[idx].required };
          return updated;
        });
      });
    },
    [preserveFocusAndPosition]
  );

  const setAll = useCallback(
    (property, value) => {
      const currentActiveElement = document.activeElement;
      preserveFocusAndPosition("bulk", String(property), () => {
        setColumns((prev) =>
          prev.map((col) => {
            if (property === "visible") {
              return { ...col, visible: !!value };
            }
            if (property === "filterable" || property === "sortable") {
              if (!col.visible) return col;
              return { ...col, [property]: value };
            }
            if (property === "pinned") {
              if (!col.visible) return col;
              return { ...col, pinned: value };
            }
            return { ...col, [property]: value };
          })
        );
      });
      setTimeout(() => {
        const bulkElement = document.querySelector(
          `[data-testid="bulk-${property}"]`
        );
        if (bulkElement && currentActiveElement === bulkElement)
          bulkElement.focus();
      }, 0);
    },
    [preserveFocusAndPosition]
  );

  // Handle Default Sort Order dropdown change (in header)
  const handleBulkDefaultSort = (sortOrder) => {
    setDefaultSortOrderAll(sortOrder);

    if (!sortOrder) {
      setSortModel(null);
      return;
    }

    const target =
      columns.find((c) => c.visible && c.sortable) ||
      columns.find((c) => c.visible);

    if (!target) {
      setSortModel(null);
      return;
    }

    if (!target.sortable) {
      setColumns((prev) =>
        prev.map((col) =>
          col.field === target.field ? { ...col, sortable: true } : col
        )
      );
    }

    setSortModel({ field: target.field, direction: sortOrder });
  };

  // PREVIEW TABLE SYNC
  const handleTableConfigChange = useCallback((updatedConfig) => {
    if (!updatedConfig || !Array.isArray(updatedConfig)) return;
    setColumns((prevColumns) => {
      const newColumns = [...prevColumns];
      updatedConfig.forEach((update) => {
        const index = newColumns.findIndex((col) => col.field === update.field);
        if (index !== -1) {
          newColumns[index] = {
            ...newColumns[index],
            ...update,
            pinned:
              update.pinned === true || update.pinned === "left"
                ? "left"
                : update.pinned === "none"
                  ? "none"
                  : newColumns[index].pinned,
          };
        }
      });
      return newColumns;
    });
  }, []);

  const handleColumnWidthChange = useCallback((field, newWidth) => {
    const roundedWidth = Math.max(40, Math.round(newWidth));
    setColumns((prev) => {
      const idx = prev.findIndex((c) => c.field === field);
      if (idx === -1) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx], width: roundedWidth };
      return updated;
    });
  }, []);

  const handleColumnVisibilityChange = useCallback(
    (field, visible) => {
      updateByField(field, "visible", visible, "visible");
    },
    [updateByField]
  );

  // FORM ACTIONS
  const handleCancel = useCallback(() => {
    setColumns([...initialColumns]);
  }, [initialColumns]);

  const handleSubmit = async () => {
    if (!org?.organization_id) {
      setSaveStatus("Organization not found.");
      return;
    }

    const currentMandatory = columns
      .filter((c) => c.required && c.field !== "id")
      .map((c) => c.field);
    const currentVisible = columns.filter((c) => c.visible).map((c) => c.field);
    const currentPinned = columns
      .filter((c) => c.pinned === "left")
      .map((c) => c.field);

    const configurationData = {
      organization_id: org.organization_id,
      columns: columns.map((c) => ({
        field: c.field,
        label: c.label || c.field.replace(/_/g, " "),
        width: Number(c.width) || 150,
        visible: !!c.visible,
        filterable: !!c.filterable,
        sortable: !!c.sortable,
        pinned: c.pinned === "left" ? "left" : "none",
        required: !!c.required,
      })),
      defaults: {
        mandatoryColumns: currentMandatory,
        defaultVisibleColumns: currentVisible,
        pinnedColumns: currentPinned,
      },
      grid: {
        sort:
          defaultSortOrderAll && columns.find((c) => c.visible && c.sortable)
            ? {
                field: columns.find((c) => c.visible && c.sortable).field,
                direction: defaultSortOrderAll,
              }
            : sortModel || null,
        filters: filterModel || {},
        pagination: { rowsPerPage: 10 },
        search: { enabled: true, placeholder: "Search...", fields: [] },
        defaultSortOrderAll,
      },
      meta: schemaDiff,
    };

    const datagridKey = datagridKeyProp;
    const payload = {
      datagrid_key: datagridKey,
      datagrid_default_configuration: configurationData,
    };

    try {
      setSaveStatus(null);
      const isUpdate = existingConfigId !== null;
      const url = isUpdate
        ? `${MAIN_URL}/api/general-datagrids/${existingConfigId}`
        : `${MAIN_URL}/api/general-datagrids`;
      const method = isUpdate ? "PUT" : "POST";

      const res = await fetch(url, {
        method: method,
        headers: getApiHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        let errorDetail = `HTTP ${res.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.errors) {
            errorDetail = Object.values(errorJson.errors).flat().join(", ");
          } else {
            errorDetail =
              errorJson.detail ||
              errorJson.message ||
              errorJson.error ||
              errorDetail;
          }
        } catch {
          errorDetail = errorText || errorDetail;
        }
        throw new Error(errorDetail);
      }

      const result = await res.json();

      if (
        !isUpdate &&
        result.datagrid?.general_datagrid_default_configuration_id
      ) {
        setExistingConfigId(
          result.datagrid.general_datagrid_default_configuration_id
        );
        setIsUpdateMode(true);
      }

      setInitialColumns([...columns]);
      setSaveStatus(
        `Configuration ${isUpdate ? "updated" : "created"} successfully.`
      );
    } catch (e) {
      setSaveStatus(`Error saving configuration: ${e.message}`);
    }
  };

  // Reset load guard when switching datagrid / API
  useEffect(() => {
    initialDataLoaded.current = false;
    setExistingConfigId(null);
    setIsUpdateMode(false);
    setSaveStatus(null);
    setShowPreview(false);
    setLoadingFields(true);
    setColumns([]);
    setInitialColumns([]);
    setRows([]);
  }, [datagridKeyProp, apiFieldsConfig]);

  // LOAD INITIAL DATA
  useEffect(() => {
    const loadInitialData = async () => {
      if (!org?.organization_id || !datagridKeyProp || initialDataLoaded.current)
        return;

      setLoadingFields(true);
      try {
        const datagridKey = datagridKeyProp;

        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: getApiHeaders(),
        });

        let savedConfig = null;
        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          savedConfig = datagrids.find((dg) => dg.datagrid_key === datagridKey);

          if (savedConfig) {
            setExistingConfigId(
              savedConfig.general_datagrid_default_configuration_id
            );
            setIsUpdateMode(true);
          }
        }

        let fieldNames = [];
        let fieldsData = null;

        if (apiFieldsConfig && Array.isArray(apiFieldsConfig)) {
          fieldNames = apiFieldsConfig.map((f) => f.field || f).filter(Boolean);
          fieldsData = apiFieldsConfig;
        } else if (apiFieldsConfig) {
          const res = await fetch(apiFieldsConfig, {
            headers: getApiHeaders(),
          });

          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();

          if (Array.isArray(data?.columns) && data.columns.length > 0) {
            fieldNames = data.columns
              .map((c) => c?.field ?? c?.accessorKey ?? c?.key)
              .filter(Boolean);
            fieldsData = data.columns;
          }

          if (fieldNames.length === 0) {
            const rowsArr = extractRowsFromResponse(data);
            const firstObjectProp = rowsArr.find(
              (r) => r && typeof r === "object" && !Array.isArray(r)
            );
            if (firstObjectProp) {
              fieldNames = Object.keys(firstObjectProp).filter(
                (k) =>
                  typeof firstObjectProp[k] !== "object" ||
                  firstObjectProp[k] === null
              );
              if (fieldNames.length === 0) {
                fieldNames = Object.keys(firstObjectProp);
              }
            }
          }
        }

        const uniqueFields = Array.from(new Set(fieldNames));

        const hasSavedColumns =
          savedConfig?.datagrid_default_configuration?.columns?.length > 0;

        if (hasSavedColumns) {
          const savedColumnMap = new Map();
          savedConfig.datagrid_default_configuration.columns.forEach((c) => {
            if (c?.field) {
              savedColumnMap.set(c.field, {
                label: c.label ?? c.field.replace(/_/g, " "),
                width: c.width ?? 150,
                visible: c.visible ?? false,
                filterable: c.filterable ?? false,
                sortable: c.sortable ?? false,
                pinned:
                  c.pinned === true || c.pinned === "left" ? "left" : "none",
                required: c.required ?? false,
              });
            }
          });

          const mergedColumns = uniqueFields.map((field) => {
            const savedProps = savedColumnMap.get(field);

            if (savedProps) {
              return { field, ...savedProps };
            }

            let label = field.replace(/_/g, " ");
            if (fieldsData && Array.isArray(fieldsData)) {
              const fieldData = fieldsData.find(
                (f) => (f.field || f) === field
              );
              if (fieldData?.label) label = fieldData.label;
            }

            return {
              field,
              label,
              width: 150,
              visible: false,
              filterable: false,
              sortable: false,
              pinned: "none",
              required: false,
            };
          });

          setColumns(mergedColumns);
          setInitialColumns([...mergedColumns]);
          const savedSort =
            savedConfig?.datagrid_default_configuration?.grid?.sort || null;
          const savedDefaultOrder =
            savedConfig?.datagrid_default_configuration?.grid
              ?.defaultSortOrderAll ||
            savedSort?.direction ||
            "";
          setDefaultSortOrderAll(savedDefaultOrder);
          if (savedSort?.field && savedSort?.direction) {
            setSortModel({
              field: savedSort.field,
              direction: savedSort.direction,
            });
          } else if (savedDefaultOrder) {
            const target =
              mergedColumns.find((c) => c.visible && c.sortable) ||
              mergedColumns.find((c) => c.visible);
            if (target) {
              setSortModel({
                field: target.field,
                direction: savedDefaultOrder,
              });
            }
          }
        } else {
          const mapped = uniqueFields.map((field) => {
            let label = field.replace(/_/g, " ");
            let width = 150;
            let visible = false;
            let required = false;
            let pinned = "none";
            let filterable = false;
            let sortable = false;

            if (fieldsData && Array.isArray(fieldsData)) {
              const fieldData = fieldsData.find(
                (f) => (f.field || f) === field
              );
              if (fieldData && typeof fieldData === "object") {
                if (fieldData.label) label = fieldData.label;
                if (fieldData.width) width = fieldData.width;
                if (typeof fieldData.visible === "boolean")
                  visible = fieldData.visible;
                if (typeof fieldData.required === "boolean")
                  required = fieldData.required;
                if (typeof fieldData.filterable === "boolean")
                  filterable = fieldData.filterable;
                if (typeof fieldData.sortable === "boolean")
                  sortable = fieldData.sortable;
                if (fieldData.pinned === "left" || fieldData.pinned === true)
                  pinned = "left";
              }
            }

            return {
              field,
              label,
              width,
              visible,
              filterable,
              sortable,
              pinned,
              required,
            };
          });

          setColumns(mapped);
          setInitialColumns([...mapped]);
        }

        initialDataLoaded.current = true;
      } catch (error) {
        console.error("Error loading initial data:", error);
        setColumns([]);
        setInitialColumns([]);
      } finally {
        setLoadingFields(false);
      }
    };

    loadInitialData();
  }, [org?.organization_id, datagridKeyProp, apiFieldsConfig]);

  // LOAD PREVIEW ROWS (cap page size — config URL may already be small)
  useEffect(() => {
    const fetchRows = async () => {
      const previewSource = apiPreviewConfig || apiFieldsConfig;
      if (
        !org?.organization_id ||
        !showPreview ||
        !previewSource ||
        Array.isArray(previewSource)
      )
        return;
      try {
        setLoadingRows(true);
        setServerError(null);

        let previewUrl = previewSource;
        try {
          const url = new URL(previewSource);
          if (!url.searchParams.has("per_page")) {
            url.searchParams.set("per_page", "25");
          }
          previewUrl = url.toString();
        } catch {
          previewUrl = previewSource;
        }

        const res = await fetch(previewUrl, {
          headers: getApiHeaders(),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setRows(extractRowsFromResponse(data));
      } catch (e) {
        setServerError(e?.message || "Failed to load rows");
        setRows([]);
      } finally {
        setLoadingRows(false);
      }
    };

    fetchRows();
  }, [org?.organization_id, showPreview, apiFieldsConfig, apiPreviewConfig]);

  const saveIsError = typeof saveStatus === "string" && saveStatus.startsWith("Error");

  if (loadingFields) {
    return (
      <div className="ems-dg-loading">
        <span className="ems-pv-spin" />
        <span>Loading {tablename || "datagrid"} fields…</span>
      </div>
    );
  }
  return (
    <div className="ems-dg-form">
      <div className="ems-card ems-pv-mode">
        <div>
          <div className="ems-pv-mode-title">
            {tablename || "DataGrid"} configuration
            {isUpdateMode ? (
              <span className="ems-pv-mode-tag">Update mode</span>
            ) : (
              <span className="ems-pv-mode-tag">New configuration</span>
            )}
          </div>
          <div className="ems-pv-mode-sub">
            Configure default columns, labels, and pin behaviour for this list.
          </div>
        </div>
        <div className="ems-dg-mode-tools">
          <div className="ems-dg-tool">
            <label className="ems-aa-label" htmlFor="ems-dg-default-sort">
              Default sort
            </label>
            <div className="ems-fm-select-wrap">
              <select
                id="ems-dg-default-sort"
                className="ems-fm-input"
                value={defaultSortOrderAll}
                disabled={visibleColumns.length === 0}
                data-testid="bulk-sortorder"
                onChange={(e) => handleBulkDefaultSort(e.target.value)}
                title={
                  visibleColumns.length === 0
                    ? "Mark at least one column as Visible first"
                    : "Default sort direction for the first visible sortable column"
                }
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value || "none"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} strokeWidth={2} />
            </div>
          </div>
          <span className="ems-dg-pin-chip">
            <Pin size={14} strokeWidth={2} />
            Left: {pinnedSummary.left}
          </span>
        </div>
      </div>

      {(schemaDiff.missingMandatory.length > 0 ||
        schemaDiff.missingVisible.length > 0 ||
        schemaDiff.missingPinned.length > 0) && (
        <div className="ems-dg-alert ems-dg-alert-warn">
          Some default settings reference fields not present in the current API
          response
          {schemaDiff.missingMandatory.length > 0
            ? ` — missing mandatory: ${schemaDiff.missingMandatory.join(", ")}`
            : ""}
          {schemaDiff.missingVisible.length > 0
            ? ` — missing visible: ${schemaDiff.missingVisible.join(", ")}`
            : ""}
          {schemaDiff.missingPinned.length > 0
            ? ` — missing pinned: ${schemaDiff.missingPinned.join(", ")}`
            : ""}
          .
        </div>
      )}

      <div className="ems-dg-bulk-bar ems-dg-mobile-only">
        <span className="ems-dg-bulk-label">Apply to all</span>
        <div className="ems-dg-bulk-toggles">
          <label className="ems-dg-bulk-item">
            <span>Visible</span>
            <EmsCheck
              checked={allVisible}
              indeterminate={someVisible && !allVisible}
              onChange={(v) => setAll("visible", v)}
              testId="bulk-visible-m"
              title="Toggle all visible"
            />
          </label>
          <label className="ems-dg-bulk-item">
            <span>Filter</span>
            <EmsCheck
              checked={allFilterable}
              indeterminate={someFilterable && !allFilterable}
              disabled={visibleColumns.length === 0}
              onChange={(v) => setAll("filterable", v)}
              testId="bulk-filterable-m"
              title="Toggle all filterable"
            />
          </label>
          <label className="ems-dg-bulk-item">
            <span>Sort</span>
            <EmsCheck
              checked={allSortable}
              indeterminate={someSortable && !allSortable}
              disabled={visibleColumns.length === 0}
              onChange={(v) => setAll("sortable", v)}
              testId="bulk-sortable-m"
              title="Toggle all sortable"
            />
          </label>
          <label className="ems-dg-bulk-item">
            <span>Pin</span>
            <button
              type="button"
              className="ems-pv-act"
              disabled={visibleColumns.length === 0}
              data-testid="bulk-pinned-m"
              title="Pin all visible columns left"
              onClick={() => setAll("pinned", "left")}
            >
              <Pin size={15} strokeWidth={2} />
            </button>
          </label>
        </div>
      </div>

      {/* Desktop: wide configuration table */}
      <div className="ems-card ems-table-card ems-dg-table-card ems-dg-desktop-only">
        <div className="ems-dg-table-wrap">
          <table className="ems-data ems-dg-table">
            <thead>
              <tr>
                <th className="ems-dg-sticky">Field name</th>
                <th>Label</th>
                <th>Width</th>
                <th className="ems-dg-col-center">
                  <span className="ems-dg-th-with-check">
                    Visible
                    <EmsCheck
                      checked={allVisible}
                      indeterminate={someVisible && !allVisible}
                      onChange={(v) => setAll("visible", v)}
                      testId="bulk-visible"
                      title="Toggle all visible"
                    />
                  </span>
                </th>
                <th className="ems-dg-col-center">
                  <span className="ems-dg-th-with-check">
                    Filterable
                    <EmsCheck
                      checked={allFilterable}
                      indeterminate={someFilterable && !allFilterable}
                      disabled={visibleColumns.length === 0}
                      onChange={(v) => setAll("filterable", v)}
                      testId="bulk-filterable"
                      title="Toggle all filterable"
                    />
                  </span>
                </th>
                <th className="ems-dg-col-center">
                  <span className="ems-dg-th-with-check">
                    Sortable
                    <EmsCheck
                      checked={allSortable}
                      indeterminate={someSortable && !allSortable}
                      disabled={visibleColumns.length === 0}
                      onChange={(v) => setAll("sortable", v)}
                      testId="bulk-sortable"
                      title="Toggle all sortable"
                    />
                  </span>
                </th>
                <th className="ems-dg-col-center">
                  <span className="ems-dg-th-with-check">
                    Pinned
                    <button
                      type="button"
                      className="ems-pv-act"
                      disabled={visibleColumns.length === 0}
                      data-testid="bulk-pinned"
                      title="Pin all visible columns left"
                      onClick={() => setAll("pinned", "left")}
                    >
                      <Pin size={15} strokeWidth={2} />
                    </button>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {columns.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="ems-pv-empty">No columns configured</div>
                  </td>
                </tr>
              ) : (
                columns.map((column) => (
                  <tr key={`col-${column.field}`}>
                    <td className="ems-dg-sticky">
                      <div className="ems-dg-field-cell">
                        <code className="ems-dg-field-name">{column.field}</code>
                        <button
                          type="button"
                          className={
                            column.required
                              ? "ems-pv-required"
                              : "ems-dg-optional"
                          }
                          disabled={!column.visible}
                          onClick={() =>
                            column.visible && toggleRequired(column.field)
                          }
                        >
                          {column.required ? "Required" : "Optional"}
                        </button>
                      </div>
                    </td>
                    <td>
                      <input
                        className="ems-fm-input ems-dg-input"
                        value={column.label}
                        placeholder="Column label"
                        data-testid={`label-${column.field}`}
                        onChange={(e) =>
                          handleLabelChange(column.field, e.target.value)
                        }
                        ref={(el) => {
                          if (el)
                            inputRefs.current[`${column.field}-label`] = el;
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min={40}
                        className="ems-fm-input ems-dg-input ems-dg-width"
                        value={column.width}
                        data-testid={`width-${column.field}`}
                        onChange={(e) =>
                          handleWidthChange(column.field, e.target.value)
                        }
                        ref={(el) => {
                          if (el)
                            inputRefs.current[`${column.field}-width`] = el;
                        }}
                      />
                    </td>
                    <td className="ems-dg-col-center">
                      <EmsCheck
                        checked={!!column.visible}
                        onChange={(v) =>
                          handleVisibilityChange(column.field, v)
                        }
                        testId={`visible-${column.field}`}
                      />
                    </td>
                    <td className="ems-dg-col-center">
                      <EmsCheck
                        checked={!!column.filterable}
                        disabled={!column.visible}
                        onChange={(v) =>
                          handleFilterableChange(column.field, v)
                        }
                        testId={`filterable-${column.field}`}
                      />
                    </td>
                    <td className="ems-dg-col-center">
                      <EmsCheck
                        checked={!!column.sortable}
                        disabled={!column.visible}
                        onChange={(v) =>
                          handleSortableChange(column.field, v)
                        }
                        testId={`sortable-${column.field}`}
                      />
                    </td>
                    <td className="ems-dg-col-center">
                      <EmsCheck
                        checked={column.pinned === "left"}
                        disabled={!column.visible}
                        onChange={(v) => handlePinnedChange(column.field, v)}
                        testId={`pinned-${column.field}`}
                        title="Pin left"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile: flat full-width field list (no cards, no sticky table) */}
      <div className="ems-dg-field-list ems-dg-mobile-only">
        <div className="ems-dg-field-list-hd">All fields</div>
        {columns.length === 0 ? (
          <div className="ems-pv-empty">No columns configured</div>
        ) : (
          columns.map((column) => (
            <div className="ems-dg-field-row" key={`m-${column.field}`}>
              <div className="ems-dg-field-card-hd">
                <code className="ems-dg-field-name ems-dg-field-name--full">
                  {column.field}
                </code>
                <button
                  type="button"
                  className={
                    column.required ? "ems-pv-required" : "ems-dg-optional"
                  }
                  disabled={!column.visible}
                  onClick={() => column.visible && toggleRequired(column.field)}
                >
                  {column.required ? "Required" : "Optional"}
                </button>
              </div>

              <div className="ems-dg-field-card-grid">
                <label className="ems-dg-field-ctrl">
                  <span className="ems-aa-label">Label</span>
                  <input
                    className="ems-fm-input ems-dg-input"
                    value={column.label}
                    placeholder="Column label"
                    data-testid={`label-m-${column.field}`}
                    onChange={(e) =>
                      handleLabelChange(column.field, e.target.value)
                    }
                    ref={(el) => {
                      if (el) inputRefs.current[`${column.field}-label`] = el;
                    }}
                  />
                </label>
                <label className="ems-dg-field-ctrl ems-dg-field-ctrl--width">
                  <span className="ems-aa-label">Width</span>
                  <input
                    type="number"
                    min={40}
                    className="ems-fm-input ems-dg-input ems-dg-width"
                    value={column.width}
                    data-testid={`width-m-${column.field}`}
                    onChange={(e) =>
                      handleWidthChange(column.field, e.target.value)
                    }
                    ref={(el) => {
                      if (el) inputRefs.current[`${column.field}-width`] = el;
                    }}
                  />
                </label>
              </div>

              <div className="ems-dg-field-flags">
                <label className="ems-dg-bulk-item">
                  <span>Visible</span>
                  <EmsCheck
                    checked={!!column.visible}
                    onChange={(v) => handleVisibilityChange(column.field, v)}
                    testId={`visible-m-${column.field}`}
                  />
                </label>
                <label className="ems-dg-bulk-item">
                  <span>Filter</span>
                  <EmsCheck
                    checked={!!column.filterable}
                    disabled={!column.visible}
                    onChange={(v) => handleFilterableChange(column.field, v)}
                    testId={`filterable-m-${column.field}`}
                  />
                </label>
                <label className="ems-dg-bulk-item">
                  <span>Sort</span>
                  <EmsCheck
                    checked={!!column.sortable}
                    disabled={!column.visible}
                    onChange={(v) => handleSortableChange(column.field, v)}
                    testId={`sortable-m-${column.field}`}
                  />
                </label>
                <label className="ems-dg-bulk-item">
                  <span>Pin</span>
                  <EmsCheck
                    checked={column.pinned === "left"}
                    disabled={!column.visible}
                    onChange={(v) => handlePinnedChange(column.field, v)}
                    testId={`pinned-m-${column.field}`}
                    title="Pin left"
                  />
                </label>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="ems-card ems-pv-actions">
        <button type="button" className="ems-btn ems-primary" onClick={handleSubmit}>
          {isUpdateMode ? "Update" : "Save"}
        </button>
        <button type="button" className="ems-btn ems-ghost" onClick={handleCancel}>
          Cancel
        </button>
        <span className="ems-pv-spacer" />
        <button
          type="button"
          className={`ems-btn ${showPreview ? "ems-primary" : "ems-ghost"}`}
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? (
            <>
              <EyeOff size={16} strokeWidth={2} /> Hide preview
            </>
          ) : (
            <>
              <Eye size={16} strokeWidth={2} /> Show preview
            </>
          )}
        </button>
      </div>

      {saveStatus && (
        <div
          className={`ems-dg-alert ${
            saveIsError ? "ems-dg-alert-error" : "ems-dg-alert-ok"
          }`}
        >
          {saveStatus}
        </div>
      )}

      {showPreview && (
        <div className="ems-card ems-pv-preview">
          <div className="ems-pv-preview-head">
            <h3 className="ems-pv-h">Live preview</h3>
            <span className="ems-pv-preview-note">
              Reflects the configuration above
            </span>
          </div>
          {serverError ? (
            <div className="ems-dg-alert ems-dg-alert-error">{serverError}</div>
          ) : null}
          {loadingRows ? (
            <div className="ems-dg-loading">
              <span className="ems-pv-spin" />
              <span>Loading preview…</span>
            </div>
          ) : null}
          <TableDataGeneric
            key={tableKey}
            tableName={tablename}
            heading={tableheading}
            primaryKey="id"
            data={rows}
            sortname={"name"}
            showActions={false}
            CardData={[]}
            hideToolbar={true}
            configss={columns}
            config={liveTableConfig}
            Route="/organization/employee/employee-details"
            token={
              typeof window !== "undefined"
                ? localStorage.getItem("token")
                : undefined
            }
            onSortChange={(field, direction) =>
              setSortModel({ field, direction })
            }
            onFilterChange={(nextFilters) => setFilterModel(nextFilters)}
            onConfigChange={handleTableConfigChange}
            onColumnWidthChange={handleColumnWidthChange}
            onColumnVisibilityChange={handleColumnVisibilityChange}
          />
        </div>
      )}
    </div>
  );
};

export default TableConfigForm;
